// Static map of every Zoho touchpoint in the app.
// Live counts/last-sync are merged in at runtime via GET /api/zoho-map/stats (keyed on `key`).

export const OAUTH_NOTE = {
  endpoint: 'POST https://accounts.zoho.com/oauth/v2/token',
  detail:
    'Both Books and CRM share one OAuth app. A stored refresh token is exchanged for a short-lived access token (cached in zoho_sync_config until ~1 min before expiry).',
  env: ['ZOHO_CLIENT_ID', 'ZOHO_CLIENT_SECRET', 'ZOHO_REFRESH_TOKEN', 'ZOHO_ORG_ID'],
};

export const PRODUCTS = [
  {
    id: 'books',
    name: 'Zoho Books',
    tag: 'Finance / Invoicing',
    api: 'books/v3',
    direction: 'PULL',
    accent: 'blue',
    badge: 'PULL ONLY',
    flowLabel: 'Financial data',
    bullets: ['Invoices', 'Payments', 'Credit Notes', 'Invoice Comments'],
    summary: 'Financial data flows into the application. Nothing is written back to Books.',
    entities: [
      {
        key: 'books_invoices',
        name: 'Invoices',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/books/v3/invoices  (list · 200/page)', '/books/v3/invoices/{id}  (detail)'],
        conditions: [
          'Full sync: date_start = 2025-04-01',
          'Incremental: last_modified_time watermark',
          'Kept only if cf_client_type == "Retail" AND status != void',
          'sort_column = date, sort_order = D',
        ],
        callMath: '1 call / 200 invoices (list) + 1 call / uncached invoice (detail, concurrency 5) + 1 call / uncached contact',
        storedIn: 'zoho_invoices  →  aggregated into training_students',
        fields: [
          { zoho: 'invoice_id', local: 'invoice_id' },
          { zoho: 'invoice_number', local: 'invoice_number' },
          { zoho: 'customer_id', local: 'customer_id' },
          { zoho: 'customer_name', local: 'customer_name', note: 'Mr./Mrs. prefix stripped' },
          { zoho: 'status', local: 'status' },
          { zoho: 'total', local: 'total' },
          { zoho: 'balance', local: 'balance' },
          { zoho: 'payment_made', local: 'payment_made' },
          { zoho: 'write_off_amount', local: 'write_off_amount' },
          { zoho: 'date', local: 'date' },
          { zoho: 'due_date', local: 'due_date' },
          { zoho: 'payment_expected_date', local: 'expected_payment_date' },
          { zoho: 'cf_original_expected_payment_date', local: 'original_expected_payment_date' },
          { zoho: 'cf_program_name', local: 'cf_program_name' },
          { zoho: 'cf_branch_name', local: 'cf_branch_name' },
          { zoho: 'cf_registration_status', local: 'cf_registration_status' },
          { zoho: 'cf_client_type', local: 'cf_client_type' },
          { zoho: 'salesperson_name', local: 'salesperson_name' },
          { zoho: 'line_items[]', local: 'line_items[]', note: 'name, quantity, rate, item_total, discount, tax_name, tax_percentage' },
          { zoho: 'sub_total', local: 'sub_total' },
          { zoho: 'tax_total', local: 'tax_total' },
          { zoho: 'discount_total', local: 'discount_total' },
        ],
        triggeredFrom: ['Sync Settings → Run Now', 'Nightly cron (~03:00 IST)', 'Global Sync pill'],
        pages: [
          { label: 'Books Analytics', slug: '/books-analytics' },
          { label: 'Revenue Report', slug: '/books-analytics/revenue-report' },
          { label: 'Students', slug: '/training/students' },
          { label: 'Overdue / Partially-paid', slug: '/training/overdue-report' },
          { label: 'Reconciliation', slug: '/training/reconciliation' },
          { label: 'Placement (candidate invoices)', slug: '/training/placement' },
        ],
      },
      {
        key: 'books_contacts',
        name: 'Contacts (Customers)',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/books/v3/contacts/{id}'],
        conditions: ['Fetched once per unique customer_id of the filtered invoices', 'Cached in zoho_contacts_cache (skipped if present)'],
        callMath: '1 call / uncached customer',
        storedIn: 'zoho_contacts_cache  →  enrollment_no on training_students',
        fields: [
          { zoho: 'cf_enrollment_no', local: 'enrollment_no', note: 'the join key to CRM' },
          { zoho: 'phone / mobile / contact_persons[]', local: 'phone', note: 'cleaned to 0XXXXXXXXXX' },
          { zoho: 'email', local: 'email' },
          { zoho: 'contact_name', local: 'contact_name' },
        ],
        triggeredFrom: ['During Books invoice sync'],
        pages: [{ label: 'Students', slug: '/training/students' }],
      },
      {
        key: 'books_payments',
        name: 'Customer Payments',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/books/v3/customerpayments  (list · 200/page)', '/books/v3/customerpayments/{id}  (detail)'],
        conditions: ['date_start = 2025-04-01 (full) or last_modified_time (incremental)', 'Detail call fetches the applied-invoices array', 'Salesperson derived from the applied invoice with the largest amount'],
        callMath: '1 call / 200 payments (list) + 1 call / payment (detail)',
        storedIn: 'zoho_payments',
        fields: [
          { zoho: 'payment_id', local: 'payment_id' },
          { zoho: 'date', local: 'date' },
          { zoho: 'amount', local: 'amount' },
          { zoho: 'amount_bcy', local: 'amount_bcy', note: 'base currency (INR)' },
          { zoho: 'exchange_rate', local: 'exchange_rate' },
          { zoho: 'status', local: 'status' },
          { zoho: 'invoices[]', local: 'invoices[]', note: 'applied: invoice_id, amount_applied' },
        ],
        triggeredFrom: ['Nightly Books sync', 'Payments full-resync endpoint'],
        pages: [
          { label: 'Books Analytics (Collections)', slug: '/books-analytics' },
          { label: 'Slip Tracker - Audit', slug: '/books-analytics/slip-tracker-audit' },
        ],
      },
      {
        key: 'books_creditnotes',
        name: 'Credit Notes',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/books/v3/creditnotes  (list · 200/page)', '/books/v3/creditnotes/{id}  (detail)'],
        conditions: ['Incremental via last_modified_time', 'Detail fetched only for CNs that have been applied (to get invoices_credited)', 'void CNs excluded from reports'],
        callMath: '1 call / 200 CNs (list) + 1 call / applied CN (detail)',
        storedIn: 'zoho_creditnotes  (+ zoho_credit_notes via training route)',
        fields: [
          { zoho: 'creditnote_id', local: 'creditnote_id' },
          { zoho: 'creditnote_number', local: 'creditnote_number' },
          { zoho: 'date', local: 'date' },
          { zoho: 'status', local: 'status' },
          { zoho: 'customer_id / customer_name', local: 'customer_id / customer_name' },
          { zoho: 'currency_code / exchange_rate', local: 'currency_code / exchange_rate' },
          { zoho: 'total / balance', local: 'total / balance' },
          { zoho: 'cf_refund_reason', local: 'refund_reason / reason', note: 'top-level reason is usually blank' },
          { zoho: 'reason_for_creditnote', local: 'reason_for_creditnote' },
          { zoho: 'reference_number', local: 'reference_number' },
          { zoho: 'invoices_credited[]', local: 'invoices_credited[]', note: 'invoice_id, invoice_number, amount, date' },
          { zoho: 'created_by_name / created_time', local: 'created_by_name / created_time' },
        ],
        triggeredFrom: ['Credit Notes page sync', 'Nightly sync'],
        pages: [
          { label: 'Credit Notes (Books)', slug: '/books-analytics/credit-notes' },
          { label: 'Credit Notes (Training)', slug: '/training/credit-notes' },
          { label: 'Reconciliation', slug: '/training/reconciliation' },
        ],
      },
      {
        key: 'books_comments',
        name: 'Invoice Comments (Slip Tracker)',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/books/v3/invoices/{id}/comments'],
        conditions: ['Backfilled across every non-void invoice', 'Idempotent — re-runnable', 'Parsed for expected-payment-date change events'],
        callMath: '1 call per non-void invoice  (heaviest job — runs in background)',
        storedIn: 'slip_expected_date_events',
        fields: [
          { zoho: 'comments[].description', local: 'event.description' },
          { zoho: 'comments[].commented_by', local: 'event.changed_by' },
          { zoho: 'comments[].date / time', local: 'event.changed_on' },
        ],
        triggeredFrom: ['Slip Tracker → Backfill comment history'],
        pages: [{ label: 'Slip Tracker - Audit', slug: '/books-analytics/slip-tracker-audit' }],
      },
      {
        key: 'books_reconciliation',
        name: 'Reconciliation Heal (on-demand)',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/books/v3/invoices/{id}', '/books/v3/creditnotes/{id}'],
        conditions: ['Triggered only when an invoice fails the balance check', 'Bulk heal capped at 50 invoices/request', 'Re-pulls fresh detail and repairs the LOCAL cache only (no write to Books)'],
        callMath: '1–2 calls per invoice being healed',
        storedIn: 'zoho_invoices / zoho_credit_notes (repaired in place)',
        fields: [
          { zoho: 'invoice (fresh)', local: 'zoho_invoices doc' },
          { zoho: 'creditnote (fresh)', local: 'invoices_credited recompute' },
        ],
        triggeredFrom: ['Reconciliation → Heal / Heal bulk'],
        pages: [{ label: 'Reconciliation', slug: '/training/reconciliation' }],
      },
    ],
  },
  {
    id: 'crm',
    name: 'Zoho CRM',
    tag: 'Leads / Batches / Placement',
    api: 'crm/v2 · crm/v6',
    direction: 'BOTH',
    accent: 'violet',
    badge: 'PULL + PUSH',
    flowLabel: 'Operational data',
    bullets: ['Leads', 'Batches', 'Students', 'Demos'],
    summary: 'Pulls leads, batches, students & demos. The only write-back is from Placement pushing Fit_to_Placement onto a Lead.',
    entities: [
      {
        key: 'crm_batches',
        name: 'Batches',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/crm/v2/Batches  (200/page)'],
        conditions: ['Smart sync: only batches with Modified_Time > last successful sync (unless force full)', 'Auto-creates Trainers, Centres & Courses masters from batch data'],
        callMath: '1 call / 200 batches',
        storedIn: 'batches  (+ trainers, centres, courses)',
        fields: [
          { zoho: 'id', local: 'zoho_batch_id' },
          { zoho: 'Name', local: 'name' },
          { zoho: 'Batch_Number', local: 'batch_number' },
          { zoho: 'Program_Name', local: 'course_name' },
          { zoho: 'Trainer_Name', local: 'trainer_name' },
          { zoho: 'Branch', local: 'centre_name' },
          { zoho: 'Batch_Status', local: 'batch_status' },
          { zoho: 'Status', local: 'status' },
          { zoho: 'Batch_Type', local: 'batch_type' },
          { zoho: 'Training_Mode', local: 'training_mode' },
          { zoho: 'Start_Date_and_Time', local: 'start_date' },
          { zoho: 'Batch_completion_Date', local: 'end_date' },
          { zoho: 'Duration', local: 'start_time / end_time (parsed)' },
          { zoho: 'Curriculum', local: 'curriculum_url' },
          { zoho: 'WhatsApp_batch_group_Link', local: 'whatsapp_link_crm' },
          { zoho: 'Audit_Trainer_Name', local: 'audit_trainer_name' },
          { zoho: 'Notes_of_Auditor', local: 'notes_of_auditor' },
          { zoho: 'Owner', local: 'owner_name' },
          { zoho: 'Modified_Time / Created_Time', local: 'crm_modified_time / crm_created_time' },
        ],
        triggeredFrom: ['Sync Settings → CRM sync', 'Global Sync pill', 'Nightly cron'],
        pages: [{ label: 'Batch Management', slug: '/training/batches' }],
      },
      {
        key: 'crm_batch_students',
        name: 'Batch Students (Enrollments)',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/crm/v2/Batches/{id}/Selected_Batch', '/crm/v2/Batches/{id}/Student  (upgrade/revision)'],
        conditions: ['Always refreshed for Running/Upcoming batches + any changed batch', 'Match: student.enrollment_no == Zoho_Books_Customer_ID', 'Lead-link mapping captured for free from each row'],
        callMath: '2 calls per active/changed batch',
        storedIn: 'training_enrollments  (+ zoho_lead_links)',
        fields: [
          { zoho: 'Zoho_Books_Customer_ID', local: 'enrollment join key' },
          { zoho: 'id', local: 'lead_id (zoho_lead_links)' },
          { zoho: 'Full_Name', local: '(mismatch reporting)' },
          { zoho: 'nd_Lead_Status', local: 'crm_lead_status' },
          { zoho: 'Batch_Status', local: 'crm_batch_status' },
          { zoho: 'Payment_Status', local: 'crm_payment_status' },
        ],
        triggeredFrom: ['CRM sync', 'Batch Management → Sync students'],
        pages: [
          { label: 'Batch Management', slug: '/training/batches' },
          { label: 'Placement', slug: '/training/placement' },
        ],
      },
      {
        key: 'crm_lead_links',
        name: 'Lead Links (search)',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/crm/v2/Leads/search?criteria=(Zoho_Books_Customer_ID:equals:{enrollment_no})'],
        conditions: ['Only for students without a cached lead_id', 'Rate-limited to 5 req/sec', 'Capped at 300/run inside CRM sync (800 standalone)'],
        callMath: '1 call per unresolved student',
        storedIn: 'zoho_lead_links { enrollment_no, lead_id }',
        fields: [{ zoho: 'data[0].id', local: 'lead_id' }],
        triggeredFrom: ['CRM sync (step 6)', 'CRM → Lead links refresh'],
        pages: [{ label: 'Student drilldowns (Open in CRM)', slug: '/training/students' }],
      },
      {
        key: 'crm_placement_pull',
        name: 'Placement Fields (bulk pull)',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/crm/v2/Leads?ids=...&fields=...  (up to 100 ids/call)'],
        conditions: ['For every linked lead_id', 'Job_Basket_User lookup flattened to display name'],
        callMath: '1 call / 100 leads',
        storedIn: 'zoho_lead_links (cached placement fields)',
        fields: [
          { zoho: 'Fit_to_Placement', local: 'fit_to_placement_cached' },
          { zoho: 'Fit_to_Placement_Date', local: 'fit_to_placement_date_cached' },
          { zoho: 'Placement_Status', local: 'placement_status' },
          { zoho: 'Placement_Date', local: 'placement_date' },
          { zoho: 'Placed_Company_Name', local: 'placed_company_name' },
          { zoho: 'Placement_Company_Job_Title', local: 'placement_company_job_title' },
          { zoho: 'Placed_Company_CTC', local: 'placed_company_ctc' },
          { zoho: 'Five_Job_Opportunities_Given', local: 'five_job_opportunities_given' },
          { zoho: 'Job_Basket_User', local: 'job_basket_user' },
          { zoho: 'Job_Basket_Onboarding_Status', local: 'job_basket_onboarding_status' },
          { zoho: 'Status_on_Job_Basket', local: 'status_on_job_basket' },
          { zoho: 'Communication_Issue', local: 'communication_issue' },
          { zoho: 'Technical_Knowledge_Issues', local: 'technical_knowledge_issues' },
          { zoho: 'JB_Team_Remarks', local: 'jb_team_remarks' },
          { zoho: 'Audit_From_Job_Basket_Team', local: 'audit_from_job_basket_team' },
          { zoho: 'Client_is_Added_in_Recruit', local: 'client_is_added_in_recruit' },
        ],
        triggeredFrom: ['Placement → Sync from CRM', 'Nightly cron (before auto-flag)'],
        pages: [{ label: 'Placement', slug: '/training/placement' }],
      },
      {
        key: 'crm_placement_push',
        name: 'Fit-to-Placement Flag',
        direction: 'PUSH',
        method: 'PUT',
        endpoints: ['/crm/v2/Leads/{id}'],
        conditions: [
          'Batch topic completion ≥ 50%',
          'Student attendance in batch ≥ 70%',
          'course_completion_status NOT in Drop / Dropped / Hold / On Hold',
          'Has a linked CRM lead',
          'Not already flagged (idempotent via placement_audit_log)',
        ],
        callMath: '1 PUT per eligible lead · rate-limited 4 req/sec',
        storedIn: 'Written to CRM Lead + mirrored to zoho_lead_links + placement_audit_log',
        source: 'Local: batch_topic_completions, daily_attendance, training_students',
        fields: [
          { zoho: 'Fit_to_Placement', local: 'true', note: 'PUSHED value' },
          { zoho: 'Fit_to_Placement_Date', local: 'today (ISO)', note: 'PUSHED value' },
        ],
        triggeredFrom: ['Placement → Flag eligible', 'Placement → Manual flag (bypasses attendance gate)', 'Nightly auto-flag cron'],
        pages: [{ label: 'Placement', slug: '/training/placement' }],
      },
      {
        key: 'crm_demos',
        name: 'Demo Records',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/crm/v2/Leads?sort_by=Modified_Time&sort_order=desc  (200/page)'],
        conditions: ['Only leads carrying a Date_of_Demo value are stored', 'Incremental via If-Modified-Since header', 'Max 15 pages/run (backfill resumes via cursor)'],
        callMath: 'Up to 15 calls/run (200 leads each)',
        storedIn: 'zoho_crm_demos',
        fields: [
          { zoho: 'id / Full_Name / Phone / Email', local: 'lead_id / full_name / phone / email' },
          { zoho: 'Enrollment_No / Zoho_Books_Customer_ID', local: 'enrollment_no / zoho_customer_id' },
          { zoho: 'Owner', local: 'owner_id / owner_name' },
          { zoho: 'Date_of_Demo', local: 'date_of_demo' },
          { zoho: 'Demo', local: 'demo_status', note: 'Sucessful / Failed / Pending' },
          { zoho: 'Tech_Call_Done_By', local: 'trainer_name' },
          { zoho: 'Action_Take_with_Date_and_Time', local: 'action_taken_at' },
          { zoho: 'Action_Time_Check', local: 'action_time_feedback' },
          { zoho: 'Action_Suggested', local: 'action_suggested' },
          { zoho: 'Formula_1', local: 'action_time_minutes' },
          { zoho: 'SM_Remarks_Hot_Admn', local: 'confirmed_admission' },
          { zoho: 'Rejected / Rejection_Applicable', local: 'rejected / rejection_applicable' },
          { zoho: 'Technology / Batch_Number / Batch', local: 'technology / batch_number / batch_ref' },
        ],
        triggeredFrom: ['CRM sync (step 7)', 'Training → CRM demos sync'],
        pages: [{ label: 'Trainer Overview V2', slug: '/training/trainer-overview-v2' }],
      },
      {
        key: 'crm_hot_leads',
        name: 'Hot Conversion Leads',
        direction: 'PULL',
        method: 'GET',
        endpoints: ['/crm/v6/Leads/search?criteria=(Lead_Hot_conversion_Date:between:a,b)  (200/page)'],
        conditions: ['Date-range window on Lead_Hot_conversion_Date', 'Matched against Books invoices/revenue for conversion rate'],
        callMath: '1 call / 200 leads per date window',
        storedIn: 'crm_hot_leads',
        fields: [
          { zoho: 'Full_Name', local: 'name' },
          { zoho: 'Owner', local: 'owner' },
          { zoho: 'Group_Name', local: 'group_name' },
          { zoho: 'nd_Lead_Status', local: 'status' },
          { zoho: 'Lead_Hot_conversion_Date', local: 'hot_date' },
          { zoho: 'Date_of_Hot_conversion_with_Time', local: 'hot_datetime' },
          { zoho: 'Zoho_Books_Customer_ID', local: 'enrolment_no' },
        ],
        triggeredFrom: ['Sales Conversion → Sync', 'Nightly cron'],
        pages: [{ label: 'Sales Conversion', slug: '/books-analytics/sales-conversion' }],
      },
    ],
  },
  {
    id: 'forms',
    name: 'Zoho Forms',
    tag: 'Inbound submissions',
    api: 'webhook',
    direction: 'INBOUND',
    accent: 'emerald',
    badge: 'INBOUND ONLY',
    flowLabel: 'Webhook submissions',
    bullets: ['Form submissions', 'Mapped fields', 'Dashboards', 'Analytics'],
    summary: 'Form submissions come into the app via webhook. No outbound calls to Forms.',
    entities: [
      {
        key: 'forms_submissions',
        name: 'Form Submissions',
        direction: 'INBOUND',
        method: 'POST',
        endpoints: ['/api/webhook/{form_slug}   (Zoho Forms → app)'],
        conditions: ['Each form has its own slug + field-mapping config', 'Raw payload kept in raw_data; mapped keys resolved via the mapping', 'Unmapped fields retained for later mapping'],
        callMath: 'Inbound — 1 request per form submission (no polling)',
        storedIn: 'submissions',
        source: 'Zoho Forms webhook payload',
        fields: [
          { zoho: 'raw_data (all form fields)', local: 'submission.raw_data' },
          { zoho: 'mapped source_field → target', local: 'submission.<mapped fields>' },
        ],
        triggeredFrom: ['Zoho Forms on submit'],
        pages: [
          { label: 'Submissions', slug: '/submissions' },
          { label: 'Webhooks', slug: '/webhooks' },
          { label: 'Form Dashboards', slug: '/form-dashboard/:formId' },
          { label: 'Cash Register', slug: '/cash-register' },
        ],
      },
    ],
  },
];

// Local MongoDB collections the app writes Zoho data into (shown in the "Your Application" hub card).
export const APP_COLLECTIONS = [
  'zoho_invoices', 'zoho_crm_demos',
  'zoho_payments', 'submissions',
  'zoho_creditnotes', 'batch_student_remarks',
];

// The single write-back path — powers the pink banner.
export const WRITE_BACK = {
  title: 'ONLY WRITE-BACK IN THE SYSTEM',
  detail: 'The Placement module updates CRM Lead fields via update_lead_fields.',
  from: 'Placement Page',
  via: 'update_lead_fields',
  to: 'Zoho CRM Lead',
  conditions: ['Syllabus ≥ 50%', 'Attendance ≥ 70%'],
};

export const INTEGRATION_RULES = [
  'Books is read-only — all data flows Zoho → App.',
  'CRM is the only bidirectional integration (Placement).',
  'Forms are webhook-driven — no outbound calls.',
  'All Zoho data is cached locally in MongoDB.',
];

// Short human blurbs for the endpoint list rows, keyed on entity.key.
export const ENTITY_BLURBS = {
  books_invoices: 'Financial data used across analytics, reports and training.',
  books_contacts: 'Enrollment number, phone & email — the join key into CRM.',
  books_payments: 'Payment data for revenue and reconciliation.',
  books_creditnotes: 'Credit notes and linked invoices.',
  books_comments: 'Invoice comment history for the collection slip audit.',
  books_reconciliation: 'On-demand re-pull to repair the local cache.',
  crm_batches: 'Batch and student information from CRM.',
  crm_batch_students: 'Enrollments mapped from CRM batch student lists.',
  crm_lead_links: 'Links each student to their CRM lead record.',
  crm_placement_pull: 'Placement status fields cached from CRM leads.',
  crm_placement_push: 'Writes Fit_to_Placement back onto the CRM lead.',
  crm_demos: 'Demo funnel data for the trainer overview.',
  crm_hot_leads: 'Hot-conversion leads for the sales conversion report.',
  forms_submissions: 'Form submissions ingested via webhook.',
};
